import { useEffect, type CSSProperties, type ReactElement } from 'react';
import { useSiteContent } from '../hooks/useSiteContent.ts';
import { LandingHeader } from '../components/landing/LandingHeader.tsx';
import { Hero } from '../components/landing/Hero.tsx';
import { TrustLogos } from '../components/landing/TrustLogos.tsx';
import { Statistics } from '../components/landing/Statistics.tsx';
import { HowItWorks } from '../components/landing/HowItWorks.tsx';
import { VideoSection } from '../components/landing/VideoSection.tsx';
import { FeatureSection } from '../components/landing/FeatureSection.tsx';
import { Matching } from '../components/landing/Matching.tsx';
import { Testimonials } from '../components/landing/Testimonials.tsx';
import { BeforeAfter } from '../components/landing/BeforeAfter.tsx';
import { FinalCta } from '../components/landing/FinalCta.tsx';
import { Faq } from '../components/landing/Faq.tsx';
import { LandingFooter } from '../components/landing/LandingFooter.tsx';
import { darken, tint } from '../lib/color.ts';
import type { SiteConfig } from '../siteContentTypes.ts';

function brandingVars(config: SiteConfig): Record<string, string> {
  const c = config.branding.colors;
  return {
    '--bg': c.background,
    '--surface': c.cardBackground,
    '--border': c.border,
    '--text': c.textPrimary,
    '--text-secondary': c.textSecondary,
    '--accent': c.accent,
    '--accent-dark': darken(c.accent),
    '--accent-tint': tint(c.accent),
    '--radius-lg': `${config.branding.style.borderRadius}px`,
    '--radius-md': `${Math.max(8, config.branding.style.borderRadius - 6)}px`,
    fontFamily: `'${config.branding.typography.primaryFont}', system-ui, sans-serif`,
  };
}

const SECTION_COMPONENTS: Record<SiteConfig['sections'][number]['key'], (config: SiteConfig) => ReactElement | null> = {
  trustLogos: (config) => <TrustLogos config={config} />,
  statistics: (config) => <Statistics config={config} />,
  howItWorks: (config) => <HowItWorks config={config} />,
  video: (config) => <VideoSection config={config} />,
  extrasSection: (config) => (
    <FeatureSection
      id="extras"
      title={config.extrasSection.title}
      subtitle={config.extrasSection.subtitle}
      image={config.extrasSection.image}
      features={config.extrasSection.features}
      ctaText={config.extrasSection.ctaText}
      ctaUrl={config.extrasSection.ctaUrl}
    />
  ),
  restaurantsSection: (config) => (
    <FeatureSection
      id="restaurateurs"
      title={config.restaurantsSection.title}
      subtitle={config.restaurantsSection.subtitle}
      image={config.restaurantsSection.image}
      features={config.restaurantsSection.features}
      ctaText={config.restaurantsSection.ctaText}
      ctaUrl={config.restaurantsSection.ctaUrl}
      reversed
    />
  ),
  matching: (config) => <Matching config={config} />,
  testimonials: (config) => <Testimonials config={config} />,
  beforeAfter: (config) => <BeforeAfter config={config} />,
  finalCta: (config) => <FinalCta config={config} />,
  faq: (config) => <Faq config={config} />,
};

function applySeo(config: SiteConfig) {
  document.title = config.seo.metaTitle || config.general.platformName;

  function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
    if (!content) return;
    let tag = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attr, name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  }

  setMeta('description', config.seo.metaDescription);
  setMeta('robots', config.seo.robots);
  setMeta('og:title', config.seo.metaTitle, 'property');
  setMeta('og:description', config.seo.metaDescription, 'property');
  if (config.seo.ogImage) setMeta('og:image', config.seo.ogImage, 'property');
}

// Rendu pur à partir d'une config déjà en mémoire : utilisé à la fois par la
// page publique (qui la récupère par fetch) et par l'aperçu en temps réel du
// back-office (qui lui passe directement l'état en cours d'édition, sans
// aller-retour réseau — c'est ce qui rend l'aperçu instantané).
export function SiteConfigRenderer({ config }: { config: SiteConfig }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', ...(brandingVars(config) as CSSProperties) }}>
      {config.branding.customCss && <style>{config.branding.customCss}</style>}
      <LandingHeader config={config} />
      <Hero config={config} />
      {config.sections
        .filter((s) => s.visible)
        .map((section) => <div key={section.key}>{SECTION_COMPONENTS[section.key](config)}</div>)}
      <LandingFooter config={config} />
    </div>
  );
}

export function LandingPage() {
  const { config, error } = useSiteContent(false);

  useEffect(() => {
    if (config) applySeo(config);
  }, [config]);

  if (error) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Impossible de charger la page.</div>;
  }
  if (!config) return null;

  return <SiteConfigRenderer config={config} />;
}
